import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api'

type Tool = 'circle' | 'arrow' | 'crop'
interface Shape { type: 'circle' | 'arrow'; x1: number; y1: number; x2: number; y2: number }
interface Rect { x: number; y: number; w: number; h: number }

const MARK = '#ffd400' // amarelo: destaca sobre a mucosa avermelhada
const SCREEN_WIDTH = 1400

// Brilho e contraste aplicados ponto a ponto (igual em qualquer navegador).
function adjust(g: CanvasRenderingContext2D, w: number, h: number, brightness: number, contrast: number) {
  if (!brightness && !contrast) return
  const lut = new Uint8ClampedArray(256)
  const k = 1 + contrast / 100
  for (let i = 0; i < 256; i++) lut[i] = (i - 128) * k + 128 + brightness * 2.55
  const data = g.getImageData(0, 0, w, h)
  const d = data.data
  for (let i = 0; i < d.length; i += 4) {
    d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]
  }
  g.putImageData(data, 0, 0)
}

function drawShape(g: CanvasRenderingContext2D, s: Shape, view: Rect, scale: number) {
  const x1 = (s.x1 - view.x) * scale, y1 = (s.y1 - view.y) * scale
  const x2 = (s.x2 - view.x) * scale, y2 = (s.y2 - view.y) * scale
  g.beginPath()
  if (s.type === 'circle') {
    g.ellipse((x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2, 0, 0, Math.PI * 2)
    g.stroke()
    return
  }
  const head = Math.max(14, g.lineWidth * 5)
  const angle = Math.atan2(y2 - y1, x2 - x1)
  g.moveTo(x1, y1)
  g.lineTo(x2, y2)
  g.stroke()
  g.beginPath()
  g.moveTo(x2, y2)
  g.lineTo(x2 - head * Math.cos(angle - 0.45), y2 - head * Math.sin(angle - 0.45))
  g.lineTo(x2 - head * Math.cos(angle + 0.45), y2 - head * Math.sin(angle + 0.45))
  g.closePath()
  g.fill()
}

// Editor de imagem do exame: marcação (círculo e seta), recorte e ajuste de
// brilho e contraste. Nunca altera o arquivo original — onSave recebe uma
// imagem nova.
export function ImageEditor({ sha, onCancel, onSave }: {
  sha: string
  onCancel: () => void
  onSave: (blob: Blob, width: number, height: number) => Promise<void>
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [img, setImg] = useState<HTMLImageElement | null>(null)
  const [tool, setTool] = useState<Tool>('circle')
  const [crop, setCrop] = useState<Rect | null>(null)
  const [shapes, setShapes] = useState<Shape[]>([])
  const [draft, setDraft] = useState<Shape | null>(null)
  const [brightness, setBrightness] = useState(0)
  const [contrast, setContrast] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    api.media.signedUrl(sha).then((url) => {
      const image = new Image()
      image.onload = () => { if (alive) setImg(image) }
      image.onerror = () => { if (alive) setError('não foi possível abrir a imagem') }
      image.src = url
    }).catch(() => setError('não foi possível abrir a imagem'))
    return () => { alive = false }
  }, [sha])

  const view: Rect = crop ?? { x: 0, y: 0, w: img?.naturalWidth ?? 1, h: img?.naturalHeight ?? 1 }

  const paint = useCallback((canvas: HTMLCanvasElement, scale: number, live: boolean) => {
    if (!img) return
    canvas.width = Math.max(1, Math.round(view.w * scale))
    canvas.height = Math.max(1, Math.round(view.h * scale))
    const g = canvas.getContext('2d')!
    g.drawImage(img, view.x, view.y, view.w, view.h, 0, 0, canvas.width, canvas.height)
    adjust(g, canvas.width, canvas.height, brightness, contrast)
    g.lineWidth = Math.max(2, canvas.width / 320)
    g.strokeStyle = MARK
    g.fillStyle = MARK
    g.lineCap = 'round'
    g.lineJoin = 'round'
    for (const s of shapes) drawShape(g, s, view, scale)
    if (!live || !draft) return
    if (tool !== 'crop') { drawShape(g, draft, view, scale); return }
    g.setLineDash([8, 6])
    g.strokeStyle = '#fff'
    g.strokeRect((draft.x1 - view.x) * scale, (draft.y1 - view.y) * scale, (draft.x2 - draft.x1) * scale, (draft.y2 - draft.y1) * scale)
    g.setLineDash([])
  }, [img, view.x, view.y, view.w, view.h, brightness, contrast, shapes, draft, tool])

  useEffect(() => {
    if (canvasRef.current) paint(canvasRef.current, Math.min(1, SCREEN_WIDTH / view.w), true)
  }, [paint, view.w])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel() } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onCancel])

  function point(e: React.PointerEvent) {
    const r = canvasRef.current!.getBoundingClientRect()
    return {
      x: view.x + Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) * view.w,
      y: view.y + Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) * view.h,
    }
  }

  function down(e: React.PointerEvent) {
    if (!img || busy) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const p = point(e)
    setDraft({ type: tool === 'arrow' ? 'arrow' : 'circle', x1: p.x, y1: p.y, x2: p.x, y2: p.y })
  }
  function move(e: React.PointerEvent) {
    if (!draft) return
    const p = point(e)
    setDraft({ ...draft, x2: p.x, y2: p.y })
  }
  function up() {
    if (!draft) return
    const d = draft
    setDraft(null)
    const w = Math.abs(d.x2 - d.x1), h = Math.abs(d.y2 - d.y1)
    if (tool === 'crop') {
      if (w >= 40 && h >= 40) setCrop({ x: Math.min(d.x1, d.x2), y: Math.min(d.y1, d.y2), w, h })
      return
    }
    if (w + h >= 12) setShapes((list) => [...list, d]) // um clique sem arrastar não marca nada
  }

  function undo() {
    if (shapes.length > 0) setShapes(shapes.slice(0, -1))
    else if (crop) setCrop(null)
    else { setBrightness(0); setContrast(0) }
  }

  async function save() {
    if (!img || busy) return
    setBusy(true)
    setError('')
    try {
      const out = document.createElement('canvas')
      paint(out, 1, false)
      const blob = await new Promise<Blob | null>((res) => out.toBlob(res, 'image/jpeg', 0.94))
      if (!blob) throw new Error('falha ao gerar a imagem')
      await onSave(blob, out.width, out.height)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'falha ao gravar a imagem')
      setBusy(false)
    }
  }

  const changed = shapes.length > 0 || crop !== null || brightness !== 0 || contrast !== 0
  const dark: React.CSSProperties = { height: 30, background: 'transparent', color: '#e6eef3', borderColor: 'rgba(255,255,255,.18)' }
  const on: React.CSSProperties = { ...dark, background: 'rgba(255,255,255,.16)', borderColor: 'rgba(255,255,255,.5)' }
  const label: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, font: '400 11.5px/1 var(--font-sans)', color: '#cfdae1' }

  return (
    <div data-viewer="1" style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(6,9,11,.97)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 'none', minHeight: 48, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', flexWrap: 'wrap' }}>
        <button className="btn" style={tool === 'circle' ? on : dark} onClick={() => setTool('circle')}>Círculo</button>
        <button className="btn" style={tool === 'arrow' ? on : dark} onClick={() => setTool('arrow')}>Seta</button>
        <button className="btn" style={tool === 'crop' ? on : dark} onClick={() => setTool('crop')}>Recortar</button>
        <label style={{ ...label, marginLeft: 10 }}>Brilho
          <input type="range" min={-50} max={50} value={brightness} onChange={(e) => setBrightness(Number(e.target.value))} style={{ width: 120 }} />
        </label>
        <label style={label}>Contraste
          <input type="range" min={-50} max={50} value={contrast} onChange={(e) => setContrast(Number(e.target.value))} style={{ width: 120 }} />
        </label>
        <button className="btn" style={dark} onClick={undo} disabled={!changed}>Desfazer</button>
        <div style={{ flex: 1 }} />
        {error && <span style={{ font: '400 12px/1 var(--font-sans)', color: '#ff9c8f' }}>{error}</span>}
        <button className="btn" style={dark} onClick={onCancel} disabled={busy}>Cancelar</button>
        <button className="btn btn-primary" style={{ height: 30, padding: '0 18px' }} onClick={() => void save()} disabled={!changed || busy}>
          {busy ? 'Gravando…' : 'Gravar'}
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px 20px' }}>
        {img && (
          <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => setDraft(null)}
            style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 6, cursor: 'crosshair', touchAction: 'none' }} />
        )}
      </div>
    </div>
  )
}
